import {
  Component,
  OnInit,
  Input,
  AfterViewInit,
  ViewChild,
  OnDestroy,
  NgZone,
  Output,
  EventEmitter,
  AfterViewChecked,
  ChangeDetectorRef,
  ElementRef,
} from "@angular/core";
import { IndexService } from "@root/router/index/index.service";
import { Subject, fromEvent } from "rxjs";
import {
  first,
  takeUntil,
  skip,
  distinctUntilChanged,
  debounceTime,
  tap,
} from "rxjs/operators";
import { CdkScrollable } from "@angular/cdk/overlay";
import { NzTableComponent } from "ng-zorro-antd/table";
import _ from "lodash";
import { TableFixedHeaderService } from "./table-fixed-header.service";

export interface HeaderItem {
  name: string; // 如果翻译需要参数或者拼凑，先用服务翻译好，传给name，不再传i18nName
  i18nName?: string;
  width?: string | number;
  paddingLeft?: string | number;
  sortable?: boolean;
  field?: string;
  hidden?: boolean;
  column?: number;
  firstColumn?: boolean; // 对于动态展示列的表格，第一列有20px的padding-left,需要标记出来
}
@Component({
  selector: "host-table-fixed-header",
  templateUrl: "./table-fixed-header.component.html",
  styleUrls: ["./table-fixed-header.component.scss"],
})
export class TableFixedHeaderComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() headerList: Array<HeaderItem> = [];
  @Input() nzData = [];
  @Input() headerTotalWidth: number = null;
  @Input() nzLoading = false;
  @Input() nzAlign: "left" | "right" | "center" = "left";
  @Input() nzShowPagination = true;
  @Input() nzPageIndex = 1;
  @Input() nzTotal;
  @Input() nzPageSize = 20;
  @Input() showAnotherPageSize = false;
  @Input() showArrow = false;
  @Input() scrollWidth = 200;
  @Input() tableInModal = false;
  @Output() onSortChange = new EventEmitter();
  @Output() nzPageIndexChange = new EventEmitter();
  @ViewChild("table")
  nzTableComponent: any;
  @ViewChild("fixedHeader")
  fixedHeader: any;
  @ViewChild('tableBody') tableBody: ElementRef;

  showHeader = false;
  header: HTMLElement;
  headerStyle: any = { display: "none" };
  resetScrollLeft = false;
  clientRect = {
    x: 0,
    y: 0,
    width: 0,
  };
  initialDistanceToTop;
  showLeftArrow = false;
  showRightArrow = false;

  private resetTableHeaderScrollLeftRaw$ = this.tableService.resetTableHeaderScrollLeftRaw$.pipe(
    distinctUntilChanged()
  );
  private resize$ = this.indexService.resizeRaw$.pipe(
    debounceTime(500),
    tap(() => {
      this.initCalculate();
    })
  );

  private destroy$ = new Subject();

  constructor(
    private indexService: IndexService,
    private ngZone: NgZone,
    private tableService: TableFixedHeaderService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // 表格内容刷新的时候，滚动需要恢复最左位置
    this.handleReset();
    // 浏览器尺寸变换时，需要重新计算固定header的x,y,width
    this.resize$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngAfterViewInit() {
    
    this.initCalculate();
    this.indexService.scrollRaw$.pipe(first()).subscribe(() => {
      this.calculateClientRect();
      this.initialDistanceToTop = this.clientRect.y;
    });
    fromEvent(this.nzTableComponent?.elementRef?.nativeElement, "scroll")
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // 获取不到nzTableComponent.tableHeaderNativeElement，但是body一样可以带动header一起滑动
        this.fixedHeader.tableBodyNativeElement.scrollLeft = this.nzTableComponent.tableBodyNativeElement.scrollLeft;
        this.judgeShowLeftArrowOrNot();
        this.judgeShowRightArrowOrNot();
      });
  }

  ngAfterViewChecked() {
    this.cd.detectChanges();
  }

  initCalculate() {
    this.calculateClientRect();
    this.setScrollListener();
    this.judgeShowLeftArrowOrNot();
    this.judgeShowRightArrowOrNot();
  }

  handleReset() {
    this.resetTableHeaderScrollLeftRaw$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.fixedHeader.tableBodyNativeElement.scrollLeft = 0;
          this.nzTableComponent.tableBodyNativeElement.scrollLeft = 0;
          this.headerStyle = {
            display: "none",
          };
          this.tableService.nowResetTableHeaderScrollLeft(false);
          this.showLeftArrow = false;
        }
      });
  }

  calculateClientRect() {
    const tableDom = this.nzTableComponent.elementRef?.nativeElement?.getBoundingClientRect();
    _.assign(this.clientRect, {
      x: _.get(tableDom, "left"),
      y: _.get(tableDom, "top"),
      width: _.get(tableDom, "width"),
    });
  }

  setScrollListener() {
    const headerHeight = document.querySelector(".layout-header").clientHeight;
    this.indexService.scrollRaw$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((scrollable: CdkScrollable) => {
        if (scrollable) {
          if (scrollable.measureScrollOffset("top") === 0) {
            console.log(1)
            this.calculateClientRect();
            this.initialDistanceToTop = this.clientRect.y;
          }
          if (
            scrollable.measureScrollOffset("top") >=
            this.initialDistanceToTop - headerHeight
          ) {
            console.log(2)
            this.ngZone.run(() => {
              this.headerStyle = {
                top: `${headerHeight}px`,
              };
              this.fixedHeader.elementRef.nativeElement.scrollLeft = this.nzTableComponent.elementRef?.nativeElement?.scrollLeft;
            });
          } else {
            console.log(3)
            this.ngZone.run(() => {
              this.headerStyle = {
                display: "none",
              };
            });
          }
        }
      });
  }

  onClickSort(field, sort: string) {
    this.resetSortStatus(field);
    let sortBy = null;
    if (sort === "descend") {
      sortBy = "desc";
    } else if (sort === "ascend") {
      sortBy = "asc";
    }
    this.onSortChange.emit(sortBy ? { field, sortBy } : null);
  }

  private resetSortStatus(field) {
    _.forEach(this.headerList, header => {
      if (field !== header.field) {
        header.sort = null;
      }
    });
  }

  scrollLeft() {
    const bodyEle = this.tableBody?.nativeElement;
    const headEle = this.fixedHeader?.elementRef?.nativeElement;
    let newLeft =
      bodyEle.scrollLeft -
      this.scrollWidth;
    newLeft = newLeft < 0 ? 0 : newLeft;
    this.showLeftArrow = newLeft === 0 ? false : true;
    headEle.scrollLeft = newLeft;
    bodyEle.scrollLeft = newLeft;
  }

  scrollRight() {
    const bodyEle = this.tableBody?.nativeElement;
    const headEle = this.fixedHeader?.elementRef?.nativeElement;
    const newRight =
      bodyEle.scrollLeft +
      this.clientRect.width +
      this.scrollWidth;
    const newLeft =
      bodyEle.scrollLeft +
      this.scrollWidth;
    this.showRightArrow = newRight >= this.headerTotalWidth ? false : true;
    headEle.scrollLeft = newLeft;
    bodyEle.scrollLeft = newLeft;
  }

  judgeShowLeftArrowOrNot() {
    this.showLeftArrow =
      this.nzTableComponent?.tableBodyNativeElement?.scrollLeft === 0
        ? false
        : true;
  }

  judgeShowRightArrowOrNot() {
    this.showRightArrow =
      this.nzTableComponent?.tableBodyNativeElement?.scrollLeft +
        this.clientRect.width >=
      this.headerTotalWidth
        ? false
        : true;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
